import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Flex, Form, Spin, Table, TableProps, Typography } from "antd";

import { CustomButton } from "ui";
import { useStores } from "stores";
import { getColumns } from "./getColumns";
import { useNotificationContext } from "providers";
import { getRefundWallets, refundCurrency } from "services";
import { BID_REQUEST_STATUS_ENUM, NOTIFICATION_TYPE_ENUM, SenderAmountDto } from "interfaces";
import { formatBalance, parseErrorMessage } from "utils";

import styles from "./refundCurrency.module.css";

export const RefundCurrencyModal = observer(() => {
  const { actionModals, bidCard } = useStores();
  const { openNotification } = useNotificationContext();

  const [form] = Form.useForm();

  const { id, currency: bidCurrency, status, balance } = bidCard.bid;

  const currency = bidCurrency ?? "USDT";

  const [loading, setLoading] = useState(false);
  const [refundWallets, setRefundWallets] = useState<SenderAmountDto[]>([]);
  const [selectedWallet, setSelectedWallet] = useState<string | null>(null);

  const handleCloseModal = () => {
    actionModals.closeAll();
  };

  useEffect(() => {
    if (id) {
      const fetchRefundWallets = async () => {
        setLoading(true);
        await getRefundWallets({ id })
          .then((wallets) => {
            setRefundWallets(wallets);
            if (wallets.length === 1) {
              setSelectedWallet(wallets[0].sender);
            }
          })
          .catch((e) => {
            openNotification({
              description: parseErrorMessage(e),
              type: NOTIFICATION_TYPE_ENUM.ERROR,
            });
          })
          .finally(() => setLoading(false));
      };

      fetchRefundWallets();
    }
  }, [id, openNotification]);

  const refundCurrencyHandler = async () => {
    setLoading(true);
    await refundCurrency({ id }, selectedWallet)
      .then(() => {
        openNotification({
          message: "Возврат средств инициирован",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        setTimeout(() => {
          bidCard.updateCardById(id);
          setLoading(false);
        }, 1500);
        handleCloseModal();
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        setLoading(false);
      });
  };

  const rowSelection: TableProps<SenderAmountDto>["rowSelection"] = {
    selectedRowKeys: selectedWallet ? [selectedWallet] : [],
    onChange: (selectedRowKeys: React.Key[], selectedRows: SenderAmountDto[]) => {
      setSelectedWallet(selectedRows[0].sender);
    },
    type: "radio",
  };

  const getSubmitButtonText = () => {
    const refundFundsText = "Вернуть средства";
    const returnToAll = "Вернуть всем";
    if (refundWallets.length === 1 || selectedWallet || status === BID_REQUEST_STATUS_ENUM.COMPLETED) {
      return refundFundsText;
    }
    if (!selectedWallet && refundWallets.length > 1) {
      return returnToAll;
    }
    return;
  };

  const getSubmitButtonTooltip = () => {
    const notSelectedWallet = "Выберите адрес для возврата";
    const everyOneGetTheirMoney = "Каждый адрес получит назад сумму своего пополнения";
    const selectedWalletGetAllMoney = "Вся сумма баланса будет направлена на выбранный адрес";

    if (!selectedWallet && refundWallets.length > 1) {
      if (status === BID_REQUEST_STATUS_ENUM.COMPLETED) {
        return notSelectedWallet;
      } else {
        return everyOneGetTheirMoney;
      }
    }

    if (selectedWallet && refundWallets.length > 1) {
      return selectedWalletGetAllMoney;
    }

    return undefined;
  };

  const getDisabledPropForSubmitButton = () => {
    return loading || (status === BID_REQUEST_STATUS_ENUM.COMPLETED && !selectedWallet);
  };

  return (
    <Spin spinning={loading}>
      <Form form={form} name="refundCurrency" onFinish={refundCurrencyHandler}>
        <Flex vertical gap="middle">
          <Form.Item name="wallet" className="full-width">
            {refundWallets.length > 0 ? (
              <Table
                size="small"
                rowKey="sender"
                rowSelection={refundWallets.length > 1 ? rowSelection : undefined}
                dataSource={refundWallets}
                columns={getColumns(currency)}
                pagination={false}
              />
            ) : (
              <Typography.Text>Кошельки для возврата отсутствуют</Typography.Text>
            )}
          </Form.Item>

          {balance && balance.token && (
            <Flex justify="space-between">
              <Typography.Text className={styles.balance}>Подлежит возврату</Typography.Text>

              <Typography.Text className={styles.balance}>
                {`${formatBalance(balance.token, currency)} ${currency}`}
              </Typography.Text>
            </Flex>
          )}
          <Form.Item>
            <Flex gap="large">
              <CustomButton
                htmlType="submit"
                disabled={getDisabledPropForSubmitButton()}
                loading={loading}
                tooltip={getSubmitButtonTooltip()}
              >
                {getSubmitButtonText()}
              </CustomButton>
              <CustomButton
                htmlType="button"
                disabled={loading}
                loading={loading}
                type="default"
                onClick={handleCloseModal}
              >
                Отмена
              </CustomButton>
            </Flex>
          </Form.Item>
        </Flex>
      </Form>
    </Spin>
  );
});
